import {
	createRootRoute,
	createRoute,
	createRouter,
	Outlet,
	type AnyRoute,
	type AnyRootRoute,
	type RouteComponent,
} from '@tanstack/react-router';

import config from '@/config';
import { basepath } from '@/constants';
import { NotFound, RootRouteComponent } from './RootRoute';
import { DevTools } from '@/app/DevTools';
import { SessionTracker } from '@/app/session/SessionTracker';

import {
	buildRouteCatalog,
	buildGuardedIssueRoutes,
	type RouteCatalogKey,
	type GuardedIssueAdapter,
} from './routing/routeCatalog';

type CreateRouteOptions = Parameters<typeof createRoute>[0];

export interface RouteSpec {
	path: string;
	component: RouteComponent;
	validateSearch?: CreateRouteOptions['validateSearch'];
	/**
	 * Every real caller ignores the beforeLoad context (`() =>
	 * ensureAuthenticated(basename)`), so this is intentionally looser than
	 * TanStack's own per-route-generic `beforeLoad` type -- that type is
	 * parented to a *specific* route's generics and doesn't structurally
	 * match across different parent routes the way this manifest field needs
	 * to.
	 */
	beforeLoad?: () => unknown;
}

export interface TenantAppManifest {
	home: RouteSpec;
	/** Catalog route names this template opts into (see routing/routeCatalog.tsx). */
	routes?: RouteCatalogKey[];
	/**
	 * Opts into the shared Cognito-`beforeLoad`-gated issue/issueArticle pair
	 * (basic/magazine/magshop) -- see `buildGuardedIssueRoutes`'s doc comment.
	 * Mutually exclusive with putting `'issue'`/`'issueArticle'` in `routes`
	 * (those are the unauthenticated/token-guarded variant).
	 */
	guardedIssue?: GuardedIssueAdapter;
	/**
	 * Template-exclusive routes -- the escape hatch for routes that don't
	 * exist in ≥2 templates (improvements/07-template-architecture.md:
	 * "not all templates have all the routes"). Each template's own
	 * App.router.tsx builds these with plain `createRoute` calls, exactly as
	 * before this factory existed, and hands the finished route objects here.
	 */
	custom?: (rootRoute: AnyRootRoute) => AnyRoute[];
	/** Renders `<DevTools router={router} />` inside the root route, like basic/morningstar/daily/starmagazine/toyota did by hand. Default false. */
	devtools?: boolean;
	/** Renders `<SessionTracker />` inside the root route, like daily/starmagazine/toyota did by hand. Default false. */
	sessionTracker?: boolean;
}

/**
 * Shared "core app" factory (improvements/07-template-architecture.md step 3):
 * assembles a router from a template's manifest instead of every template
 * hand-rolling its own createRootRoute/createRoute/createRouter tree.
 *
 * `drafts` stays admin-gated and `favorite` stays favorite-gated here
 * exactly as they were in every per-template router before this factory
 * existed (`if (config?.app?.admin) routes.push(draftsRoute)` /
 * `if (config?.app?.favorite) routes.push(favoriteRoute)`).
 */
export function createTenantApp(manifest: TenantAppManifest) {
	let router: ReturnType<typeof createRouter>;

	// Matches the local RootRouteComponent every one of
	// basic/daily/morningstar/starmagazine/toyota hand-rolled before this
	// factory existed -- plain Outlet + optional SessionTracker/DevTools, not
	// the shared app/RootRoute.tsx one (which has its own, different
	// devtools toggle and would double up with DevTools below).
	function TenantRootRouteComponent() {
		return (
			<>
				{manifest.sessionTracker && <SessionTracker />}
				<Outlet />
				{manifest.devtools && <DevTools router={router} />}
			</>
		);
	}

	const rootRoute = createRootRoute({
		component:
			manifest.devtools || manifest.sessionTracker
				? TenantRootRouteComponent
				: RootRouteComponent,
	});

	const catalog = buildRouteCatalog(rootRoute);
	const guarded = manifest.guardedIssue
		? buildGuardedIssueRoutes(rootRoute, manifest.guardedIssue)
		: undefined;

	const homeRoute = createRoute({
		getParentRoute: () => rootRoute,
		path: manifest.home.path,
		component: manifest.home.component,
		validateSearch: manifest.home.validateSearch,
		beforeLoad: manifest.home.beforeLoad,
	});

	const routeNames = (manifest.routes ?? []).filter((name) => {
		if (name === 'drafts') return !!config?.app?.admin;
		if (name === 'favorite') return !!config?.app?.favorite;
		return true;
	});
	const catalogRoutes = routeNames.map((name) => catalog[name]);
	const guardedRoutes = guarded
		? [guarded.issueGuarded, guarded.issueArticleGuarded]
		: [];
	const customRoutes = manifest.custom?.(rootRoute) ?? [];

	const routeTree = rootRoute.addChildren([
		homeRoute,
		...catalogRoutes,
		...guardedRoutes,
		...customRoutes,
	]);

	router = createRouter({
		routeTree,
		basepath,
		defaultNotFoundComponent: NotFound,
	});

	return router;
}
